import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewEncapsulation, ElementRef, Input, ViewChild, SimpleChanges, OnChanges, Output, EventEmitter, OnDestroy, signal } from '@angular/core';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { MediaObjectFit, MediaSource } from '@newroom-connect/library/types';
import { ArrayHelper } from '@newroom-connect/library/helpers';
import { VideoPlayerEventType } from '@newroom-connect/library/enums';

import { LoadingSpinnerComponent } from '../loading/loading-spinner/loading-spinner.component';
import { IconComponent } from '../icon/icon.component';
@Component({
  standalone: true,
  imports: [
    CommonModule,
    LoadingSpinnerComponent,
    IconComponent
  ],
  selector: 'nrc-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class VideoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) public sources: MediaSource[] = [];
  @Input() public controls = true;
  @Input() public autoplay = false;
  @Input() public muted = false;
  @Input() public loop = false;
  @Input() public playsInline = false;
  @Input() public fluid = false;
  @Input() public disablePictureInPicture = false;
  @Input() public objectFit: MediaObjectFit = 'contain';
  @Input() public preload: 'auto' | 'metadata' | 'none' = 'metadata';
  @Input() public isRounded = false;

  @Output() public loadComplete = new EventEmitter<void>();
  @Output() public videoPlayEvent = new EventEmitter<number>();
  @Output() public videoEndEvent = new EventEmitter<void>();
  @Output() public videoDuration = new EventEmitter<number | undefined>();
  @Output() public videoPauseEvent = new EventEmitter<number>();
  @Output() public videoSeekEvent = new EventEmitter<number>();

  @ViewChild('videoPlayer', { static: false }) public videoPlayerRef!: ElementRef<HTMLVideoElement>;

  public player: Player | null = null;
  public hasLoadedData = signal<boolean>(false);
  public hasError = signal<boolean>(false);
  public isBuffering = signal<boolean>(false);

  /**
   * We call the load method of the video element on changing the sources of the component, so that the element is updated
   * with the new sources.
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['sources'] && !changes['sources'].firstChange && this.videoPlayerRef) {
      if (this.player) {
        this.player.pause();
        this.player.reset();

        // No need to reinitialize the player when it already exists
        // Just reset state and update sources
        this.resetVideoState();

        // Add sources with query parameter
        this.addSourcesToPlayer();

        this.player.load();

        if (!this.autoplay) {
          this.showPlayButton();
        }

        return;
      }

      // Only initialize video if player doesn't exist
      this.initializeVideo();
    }
  }

  /**
   * Clean up resources when component is destroyed.
   */
  public ngOnDestroy(): void {
    this.resetVideoState();

    if (this.player) {
      this.player.dispose();
    }
  }

  /**
   * Initialize video after the view is initialized.
   */
  public ngAfterViewInit(): void {
    if (!this.sources.length || !ArrayHelper.isNotEmpty(this.sources)) {
      return;
    }

    this.initializeVideo();
  }

  /**
   * Reset all video state signals to their initial values.
   */
  private resetVideoState(): void {
    this.hasLoadedData.set(false);
    this.hasError.set(false);
    this.isBuffering.set(true);
  }

  /**
   *
   */
  private addSourcesToPlayer(): void {
    if (!this.player) {
      return;
    }

    // Add sources to the player for each source in the sources array.
    for (const source of this.sources) {
      this.player.addSourceElement(source.uri, source.mimetype);
    }
  }

  /**
   * Initializes the Video.js player with the provided video element.
   */
  private initializeVideo(): void {
    if (!this.videoPlayerRef) {
      return;
    }

    this.resetVideoState();

    this.player = videojs(this.videoPlayerRef.nativeElement, {
      controls: this.controls,
      autoplay: this.autoplay,
      muted: this.muted,
      loop: this.loop,
      playsInline: this.playsInline,
      fluid: this.fluid,
      disablePictureInPicture: this.disablePictureInPicture,
      preload: this.preload,
      responsive: true
    });

    // Add sources with the bypass cache parameter
    this.addSourcesToPlayer();

    if (!this.autoplay) {
      this.showPlayButton();
    }

    this.setupEventListeners();
  }

  /**
   * Set up all video player event listeners.
   */
  private setupEventListeners(): void {
    if (!this.player) {
      return;
    }

    // Loading and buffering events
    this.player.on(VideoPlayerEventType.LOAD_START, () => {
      this.isBuffering.set(true);
      this.hidePlayButton();
    });

    this.player.on(VideoPlayerEventType.WAITING, () => {
      this.isBuffering.set(true);
    });

    this.player.on(VideoPlayerEventType.PLAY, () => {
      const currentTime = this.player?.currentTime();

      if (currentTime && currentTime >= 0) {
        this.videoPlayEvent.emit(currentTime);
      }
    });

    // Playback state events
    this.player.on(VideoPlayerEventType.PLAYING, () => {
      this.isBuffering.set(false);
      this.hidePlayButton();
    });

    this.player.on(VideoPlayerEventType.PAUSE, () => {
      this.isBuffering.set(false);
      this.showPlayButton();

      this.videoPauseEvent.emit(this.player?.currentTime());
    });

    // Error handling
    this.player.on(VideoPlayerEventType.ERROR, () => {
      this.hasError.set(true);
      this.hasLoadedData.set(false);
      this.isBuffering.set(false);
    });

    // Video is fully loaded
    this.player.on(VideoPlayerEventType.LOADED_DATA, () => {
      this.hasLoadedData.set(true);
      this.hasError.set(false);
      this.isBuffering.set(false);

      this.setPlayerElements();
      this.loadComplete.emit();

      if (!this.autoplay) {
        this.showPlayButton();
      }
    });

    this.player.on(VideoPlayerEventType.LOADED_METADATA, () => {
      const duration = this.player?.duration();

      this.videoDuration.emit(duration);
    });

    this.player.on(VideoPlayerEventType.ENDED, () => {
      this.videoEndEvent.emit();
    });

    // Seek event
    this.player.on(VideoPlayerEventType.SEEKED, () => {
      this.videoSeekEvent.emit(this.player?.currentTime());
    });
  }

  /**
   * Adjusts the size of the player controls (button and spinner) when the video element is smaller than 200 pixels.
   */
  public setPlayerElements(): void {
    if (!this.videoPlayerRef) {
      return;
    }

    const playerEl = this.videoPlayerRef.nativeElement;

    playerEl.classList.add(this.objectFit === 'cover' ? 'object-cover' : 'object-contain');

    if (playerEl.offsetWidth < 200) {
      playerEl.childNodes.forEach((child) => {
        const childEl = child as HTMLElement;

        if (!childEl || !childEl.classList || !childEl.tagName) {
          return;
        }

        // Make the loading spinner smaller.
        if (childEl.classList.contains('vjs-loading-spinner')) {
          childEl.classList.add('vjs-loading-spinner-small');
        }

        // Hide the control bar.
        if (childEl.classList.contains('vjs-control-bar')) {
          childEl.classList.add('controls-hide');
        }

        // Make the play button smaller.
        if (childEl.classList.contains('vjs-big-play-button')) {
          childEl.classList.add('vjs-big-play-button-small');
        }
      });
    }
  }

  private hidePlayButton(): void {
    if (!this.player) {
      return;
    }

    // Get the play button using Video.js API which is more reliable
    const playerEl = this.player.el();
    const playButton = playerEl?.querySelector('.vjs-big-play-button') as HTMLElement;

    if (!playButton) {
      return;
    }

    playButton.style.display = 'none';
  }

  private showPlayButton(): void {
    if (!this.player) {
      return;
    }

    // Get the play button using Video.js API which is more reliable
    const playerEl = this.player.el();
    const playButton = playerEl?.querySelector('.vjs-big-play-button') as HTMLElement;

    if (!playButton) {
      return;
    }

    playButton.style.display = 'grid';
  }
}
